// src/pages/Careers.jsx
import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  TextField,
  Chip,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import {
  WorkOutline,
  People,
  School,
  TrendingUp,
  EmojiEvents,
  LocationOn,
  AccessTime,
  AttachMoney,
  Email,
  Phone,
  LinkedIn,
  Facebook,
  Twitter,
  Send,
  CheckCircle,
  Diamond,
  BusinessCenter,
  Favorite,
  Lightbulb,
  Group
} from '@mui/icons-material';
import { motion } from "framer-motion";

// Professional DiamondBank Zimbabwe color palette
const colors = {
  primary: '#1a1a1a',
  primaryLight: '#2d2d2d',
  primaryDark: '#0d0d0d',
  accent: '#6b21a5',
  accentLight: '#8b5cc9',
  accentDark: '#4c1d6e',
  secondary: '#e5e7eb',
  white: '#ffffff',
  black: '#000000',
  gray: '#6b7280',
  grayLight: '#9ca3af',
  grayDark: '#374151',
  lightBg: '#f8f9fa',
  textDark: '#1a1a1a',
  textLight: '#ffffff'
};

const Careers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setSnackbarOpen(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        position: '',
        message: ''
      });
      setTimeout(() => setSubmitted(false), 3000);
    }, 1500);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const benefits = [
    { icon: <AttachMoney sx={{ fontSize: 32 }} />, title: "Competitive Salary", description: "Attractive compensation packages in ZWL" },
    { icon: <School sx={{ fontSize: 32 }} />, title: "Learning & Development", description: "Continuous learning opportunities" },
    { icon: <EmojiEvents sx={{ fontSize: 32 }} />, title: "Performance Bonuses", description: "Recognition for excellence" },
    { icon: <AccessTime sx={{ fontSize: 32 }} />, title: "Flexible Hours", description: "Work-life balance" },
    { icon: <Favorite sx={{ fontSize: 32 }} />, title: "Health Insurance", description: "Comprehensive health coverage" },
    { icon: <TrendingUp sx={{ fontSize: 32 }} />, title: "Career Growth", description: "Fast-track promotions in Zimbabwe" }
  ];

  const values = [
    { icon: <Lightbulb />, title: "Innovation", description: "We embrace new ideas and technologies" },
    { icon: <Group />, title: "Collaboration", description: "Teamwork makes the dream work" },
    { icon: <EmojiEvents />, title: "Excellence", description: "Striving for the best always" },
    { icon: <Favorite />, title: "Integrity", description: "Doing the right thing" }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ overflowX: 'hidden', backgroundColor: colors.lightBg }}>
        {/* Hero Section */}
        <Box
          sx={{
            height: '50vh',
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${colors.primary}dd 0%, ${colors.accent}99 100%)`,
            color: 'white',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.08,
            backgroundImage: `radial-gradient(circle at 20% 50%, ${colors.white} 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }} />
          
          <Container>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <Diamond sx={{ fontSize: 60, color: colors.accent, mb: 2, animation: 'spin 10s linear infinite' }} />
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  mb: 2
                }}
              >
                Join Our <span style={{ color: colors.accent }}>Team</span>
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  maxWidth: '700px',
                  mx: 'auto',
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  fontWeight: 400,
                  opacity: 0.9
                }}
              >
                Build your career with DiamondBank Zimbabwe and help shape the future of banking in Zimbabwe
              </Typography>
            </motion.div>
          </Container>
        </Box>

        <Container sx={{ py: 8 }}>
          {/* Why Join Us Section */}
          <Box sx={{ mb: 8 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="subtitle1" sx={{ color: colors.accent, fontWeight: 600, letterSpacing: 1, mb: 1 }}>
                WHY JOIN US
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: colors.primary,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80px',
                    height: '3px',
                    background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                    borderRadius: '2px'
                  }
                }}
              >
                Benefits & Perks
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {benefits.map((benefit, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        borderRadius: '16px',
                        background: 'white',
                        boxShadow: `0 4px 16px ${alpha(colors.primary, 0.05)}`,
                        border: `1px solid ${alpha(colors.gray, 0.1)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 8px 24px ${alpha(colors.accent, 0.1)}`,
                          borderColor: alpha(colors.accent, 0.2)
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          backgroundColor: alpha(colors.accent, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          mx: 'auto',
                          color: colors.accent
                        }}
                      >
                        {benefit.icon}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: colors.primary, mb: 1 }}>
                        {benefit.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.gray }}>
                        {benefit.description}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Our Values Section */}
          <Box sx={{ mb: 8 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="subtitle1" sx={{ color: colors.accent, fontWeight: 600, letterSpacing: 1, mb: 1 }}>
                OUR VALUES
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: colors.primary,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80px',
                    height: '3px',
                    background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                    borderRadius: '2px'
                  }
                }}
              >
                What We Stand For
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {values.map((value, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        borderRadius: '16px',
                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                        color: 'white',
                        height: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: `0 12px 28px ${alpha(colors.accent, 0.3)}`
                        }
                      }}
                    >
                      <Box sx={{ fontSize: 40, color: colors.accent, mb: 2 }}>{value.icon}</Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        {value.title}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {value.description}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Open Positions - Coming Soon */}
          <Box sx={{ mb: 8 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="subtitle1" sx={{ color: colors.accent, fontWeight: 600, letterSpacing: 1, mb: 1 }}>
                CURRENT OPENINGS
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: colors.primary,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80px',
                    height: '3px',
                    background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                    borderRadius: '2px'
                  }
                }}
              >
                Open Positions
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                textAlign: 'center',
                borderRadius: '20px',
                background: `linear-gradient(135deg, ${alpha(colors.accent, 0.05)} 0%, ${alpha(colors.primary, 0.05)} 100%)`,
                border: `1px solid ${alpha(colors.accent, 0.2)}`
              }}
            >
              <BusinessCenter sx={{ fontSize: 70, color: colors.accent, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, color: colors.primary, mb: 2, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                No Open Positions at This Time
              </Typography>
              <Typography variant="body1" sx={{ color: colors.gray, maxWidth: '600px', mx: 'auto', mb: 3 }}>
                We're always looking for talented individuals to join our team in Zimbabwe. 
                Submit your application below, and we'll contact you when positions become available.
              </Typography>
              <Chip
                label="Accepting Applications"
                sx={{
                  backgroundColor: colors.accent,
                  color: colors.white,
                  fontWeight: 600
                }}
              />
            </Paper>
          </Box>

          {/* Application Form */}
          <Box sx={{ mb: 8 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="subtitle1" sx={{ color: colors.accent, fontWeight: 600, letterSpacing: 1, mb: 1 }}>
                EXPRESS INTEREST
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: colors.primary,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80px',
                    height: '3px',
                    background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                    borderRadius: '2px'
                  }
                }}
              >
                Submit Your Application
              </Typography>
              <Typography variant="body1" sx={{ color: colors.gray, maxWidth: '600px', mx: 'auto', mt: 2 }}>
                Send us your details and we'll keep you in mind for future opportunities in Zimbabwe
              </Typography>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ x: -30, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius: '20px',
                      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                      color: 'white',
                      height: '100%'
                    }}
                  >
                    <Diamond sx={{ fontSize: 40, color: colors.accent, mb: 2 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                      Why Work With Us?
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                      At DiamondBank Zimbabwe, we believe in investing in our people. Join a team that values innovation, 
                      integrity, and excellence.
                    </Typography>
                    <Divider sx={{ my: 3, borderColor: alpha(colors.white, 0.2) }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LocationOn sx={{ color: colors.accent, mr: 2 }} />
                      <Typography variant="body2">Harare, Zimbabwe</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AccessTime sx={{ color: colors.accent, mr: 2 }} />
                      <Typography variant="body2">Full-time & Part-time Positions</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <School sx={{ color: colors.accent, mr: 2 }} />
                      <Typography variant="body2">Ongoing Training & Development</Typography>
                    </Box>
                  </Paper>
                </motion.div>
              </Grid>

              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ x: 30, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius: '20px',
                      background: 'white',
                      boxShadow: `0 8px 24px ${alpha(colors.primary, 0.08)}`,
                      border: `1px solid ${alpha(colors.gray, 0.1)}`
                    }}
                  >
                    {submitted ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CheckCircle sx={{ fontSize: 60, color: colors.accent, mb: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary, mb: 2 }}>
                          Application Submitted!
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.gray }}>
                          Thank you for your interest in DiamondBank Zimbabwe. We'll review your application and contact you when positions become available.
                        </Typography>
                      </Box>
                    ) : (
                      <form onSubmit={handleSubmit}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary, mb: 3 }}>
                          Tell Us About Yourself
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Full Name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '12px',
                                  '&:hover fieldset': {
                                    borderColor: colors.accent
                                  }
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Email Address"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '12px',
                                  '&:hover fieldset': {
                                    borderColor: colors.accent
                                  }
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Phone Number"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '12px',
                                  '&:hover fieldset': {
                                    borderColor: colors.accent
                                  }
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Position Interested In"
                              name="position"
                              value={formData.position}
                              onChange={handleChange}
                              variant="outlined"
                              placeholder="e.g., Banking Officer, IT Specialist, Customer Service"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '12px',
                                  '&:hover fieldset': {
                                    borderColor: colors.accent
                                  }
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Additional Information"
                              name="message"
                              value={formData.message}
                              onChange={handleChange}
                              multiline
                              rows={4}
                              variant="outlined"
                              placeholder="Tell us about your experience, skills, and why you'd like to join DiamondBank Zimbabwe"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '12px',
                                  '&:hover fieldset': {
                                    borderColor: colors.accent
                                  }
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              type="submit"
                              fullWidth
                              variant="contained"
                              disabled={loading}
                              sx={{
                                py: 1.5,
                                borderRadius: '50px',
                                background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`,
                                fontWeight: 600,
                                fontSize: '1rem',
                                '&:hover': {
                                  background: colors.accentLight,
                                  transform: 'translateY(-2px)'
                                },
                                transition: 'all 0.3s ease'
                              }}
                            >
                              {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Application'}
                              {!loading && <Send sx={{ ml: 1, fontSize: 18 }} />}
                            </Button>
                          </Grid>
                        </Grid>
                      </form>
                    )}
                  </Paper>
                </motion.div>
              </Grid>
            </Grid>
          </Box>

          {/* Contact Section - Updated for Zimbabwe */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: '20px',
              backgroundColor: colors.white,
              border: `1px solid ${alpha(colors.gray, 0.1)}`,
              textAlign: 'center'
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, color: colors.primary, mb: 2 }}>
              Have Questions?
            </Typography>
            <Typography variant="body1" sx={{ color: colors.gray, mb: 3 }}>
              Reach out to our HR team for any inquiries about careers at DiamondBank Zimbabwe
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email sx={{ color: colors.accent }} />
                <Typography variant="body2">careers@diamondbank.co.zw</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone sx={{ color: colors.accent }} />
                <Typography variant="body2">+263 242 708 888</Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Application submitted successfully! We'll contact you soon.
        </Alert>
      </Snackbar>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </motion.div>
  );
};

export default Careers;